// Page Path : src\pages\design\document\configuration\right-pane.tsx
import { RootState } from 'store';
import { Box, CircularProgress, Dialog, Stack, Alert } from '@mui/material';
import CanvasToolbar from './toolbar';
import useFabricCanvas from 'hooks/useFabricCanvas';
import { FormikProps } from 'formik';
import { forwardRef, MutableRefObject, useEffect, useRef, useState } from 'react';
import { CloseOutlined, DoneOutlined, InfoOutlined } from '@mui/icons-material';
import { Tooltip } from 'components/elements';
import { useSelector } from 'react-redux';
import FabricImageCanvas from './canvas';
import {
  CavseRefType,
  ImageViewerGetDocumentBytesResponse,
  ImageViwerNavigateFile,
  RectMetricsForRubberBandType,
  SearchTextInFileResponse
} from 'types/image-viewer';
import IconButtonExtended from 'components/@extended/IconButton';
import { generateMetricsFromROI, generateROIStringFromMetrics } from 'utils/imageViewerUtils';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  VerificationPatternsListTypes,
  VerifiactionFileDetailsTypes,
  VerificationBatchListTypes,
  VerificationTechniquesType
} from 'types/verification';
import { useLazyGetTextForROIQuery } from 'api/document';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import EditIcon from '@mui/icons-material/Edit';
import AssuranceAddPatternDialog from 'sections/review/image-viewer/AssuranceAddPatternDialog';
import useScreenSize from 'hooks/useScreenSize';
import { Titlebar } from './TitleBar';
import { useTheme } from '@mui/material';

const ScrollNavigator = ({
  onScrollUp,
  onScrollRight,
  onScrollDown,
  onScrollLeft
}: {
  onScrollUp: () => void;
  onScrollRight: () => void;
  onScrollDown: () => void;
  onScrollLeft: () => void;
}) => {
  return (
    <Box className="canvas-scroll-navigator">
      <Box className="scroll-button-wrapper">
        <Box className="scroll-button scroll-up" onClick={onScrollUp} />
        <Box className="scroll-button scroll-right" onClick={onScrollRight} />
        <Box className="scroll-button scroll-down" onClick={onScrollDown} />
        <Box className="scroll-button scroll-left" onClick={onScrollLeft} />
      </Box>
    </Box>
  );
};

interface Props {
  isSecondaryScreen?: boolean;
  currentPageNumber: number;
  setCurrentPageNumber: (pageNumber: number) => void;
  currentFileId: number; // Add current file ID state
  dualScreenWindowRef?: MutableRefObject<Window | null>;
  currentPageDetails?: ImageViewerGetDocumentBytesResponse;
  isLoadingImageBytes?: boolean;
  fileLevelDetails?: VerifiactionFileDetailsTypes;
  searchTextInFile: (_value: string) => Promise<any[]>;
  canvasFunctionRef?: { current: CavseRefType };
  handleFileNavigation: ImageViwerNavigateFile;
  patterns: VerificationPatternsListTypes[];
  selectedUser?: string;
  setSelectedUser?: any;
  finishAndMoveNextBatch: any;
  handleAddNewPattern: any;
  selectedFieldName?: string;
  selectedPattern: VerificationPatternsListTypes | null;
  batchList: VerificationBatchListTypes[];
  selectedBatchId: number;
  selectedTechniqueName: VerificationTechniquesType;
  isVerifiedBatch: boolean;
  expanded?: boolean;
}

const ImageViewerRightPane = forwardRef<HTMLElement, Props>(
  (
    {
      isSecondaryScreen,
      currentPageNumber,
      setCurrentPageNumber,
      currentFileId, // Add the current file ID
      dualScreenWindowRef,
      currentPageDetails,
      isLoadingImageBytes,
      fileLevelDetails,
      searchTextInFile,
      canvasFunctionRef,
      handleFileNavigation,
      patterns,
      selectedUser,
      setSelectedUser,
      finishAndMoveNextBatch,
      handleAddNewPattern,
      selectedFieldName,
      selectedPattern,
      batchList,
      selectedBatchId,
      selectedTechniqueName,

      isVerifiedBatch,
      expanded = false
    },
    ref
  ) => {
    const theme = useTheme();
    const patternFormRef = useRef<FormikProps<any> | null>(null);
    const [showDialog, setshowDialog] = useState<boolean>(false);
    const [addEditPatternType, setAddEditPatternType] = useState<'ADD' | 'EDIT' | false>(false);
    const [addEditPatternIsSaving, setAddEditPatternIsSaving] = useState<boolean>(false);
    const [newRoi, setNewRoi] = useState<string>('');
    const [triggerGetTextForROI] = useLazyGetTextForROIQuery(); // Lazy query for flexibility
    const searchTextRubberBandCoordsRef = useRef<RectMetricsForRubberBandType | null>(null);
    const [addInsertInitialData, setAddInsertInitialData] = useState<any>(false);
    const [headerData, setheaderData] = useState<any>(false);
    const translate = (key: string, options?: any) => (window as any).translate(key, options);
    const { isMobile, isDesktop } = useScreenSize();
    const {
      canvasRef,
      onRotateLeft,
      onRotateRight,
      setShowRubberbandDialog,
      onZoomIn,
      onZoomOut,
      onRubberbandSelect,
      onRubberbandUnSelect,
      onFitToScreenSelect,
      onFitToScreenUnSelect,
      showRubberbandDialog,
      rubberbandDialogPosition,
      onDualScreen,
      dualScreenEnabled,
      currentZoomLevel,
      isMaxZoomIn,
      isMaxZoomOut,
      showLabelRubberbandDialog,
      imageLoading,
      initCanvas,
      loadImage,
      setShowLabelRubberbandDialog,
      disableLabelRubberband,
      isLabelRubberbandEnabled,
      highlightRubberBand,
      onRubberbandDialogDismiss,
      rubberbandMetrics,
      rectImageContent,
      removeCurrentImage,
      fitToScreen
    } = useFabricCanvas({
      currentPageNumber,
      dualScreenWindowRef,
      secondaryScreenUrl: `verification-image-viewer-secondary-view/${fileLevelDetails?.docTypeId}/${fileLevelDetails?.batchID}`,
      shouldCaptureRectImgContent: true,
      enableRightClick: !isVerifiedBatch,
      expanded
    });

    useEffect(() => {
      if (canvasFunctionRef) {
        canvasFunctionRef.current = {
          highlightRubberBand
        };
      }
    }, [canvasFunctionRef, highlightRubberBand]);

    const handleOnHighlightRubberBandBySearch = (data?: SearchTextInFileResponse) => {
      if (data) {
        const _metric: RectMetricsForRubberBandType = {
          left: data.xaxis,
          top: data.yaxis,
          width: data.width,
          height: data.height,
          pageNo: data.pageNo
        };
        if (data.pageNo !== currentPageNumber) {
          setCurrentPageNumber(data.pageNo);
          searchTextRubberBandCoordsRef.current = _metric;
        } else {
          highlightRubberBand(_metric);
        }
      } else {
        highlightRubberBand(); // Remove existing
      }
    };

    const canvasWrapperRef = useRef<HTMLElement>(null);
    const currentFieldLabel = useSelector((state: RootState) => state.taggingSlice.currentFieldLabel);

    useEffect(() => {
      initCanvas();
    }, [initCanvas]);

    useEffect(() => {
      searchTextRubberBandCoordsRef.current = null;
    }, [fileLevelDetails]);

    useEffect(() => {
      if (!isLoadingImageBytes && currentPageDetails) {
        const canvasContainer = canvasWrapperRef.current?.getElementsByClassName('canvas-container')[0];
        canvasContainer?.scrollTo({ top: 0, left: 0 });
        loadImage(
          currentPageDetails?.pageBytes,
          currentPageNumber,
          searchTextRubberBandCoordsRef.current?.pageNo === currentPageNumber ? searchTextRubberBandCoordsRef.current : undefined
        );
      } else {
        removeCurrentImage();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPageDetails, isLoadingImageBytes, loadImage]);

    const onScrollUp = () => {
      const canvasContainer = canvasWrapperRef.current?.getElementsByClassName('canvas-container')[0];
      canvasContainer?.scrollTo({ top: canvasContainer.scrollTop - 64, behavior: 'smooth' });
    };

    const onScrollRight = () => {
      const canvasContainer = canvasWrapperRef.current?.getElementsByClassName('canvas-container')[0];
      canvasContainer?.scrollTo({ left: canvasContainer.scrollLeft + 150, behavior: 'smooth' });
    };

    const onScrollDown = () => {
      const canvasContainer = canvasWrapperRef.current?.getElementsByClassName('canvas-container')[0];
      canvasContainer?.scrollTo({ top: canvasContainer.scrollTop + 64, behavior: 'smooth' });
    };

    const onScrollLeft = () => {
      const canvasContainer = canvasWrapperRef.current?.getElementsByClassName('canvas-container')[0];
      canvasContainer?.scrollTo({ left: canvasContainer.scrollLeft - 150, behavior: 'smooth' });
    };

    const onConfirmUpdateFieldROI = async (mode: 'ADD' | 'EDIT' | 'footer') => {
      if (rubberbandMetrics) {
        const newROI = generateROIStringFromMetrics(rubberbandMetrics);
        setNewRoi(newROI);
        const coordinates = generateMetricsFromROI(newROI);
        const { data: roiData }: any = await triggerGetTextForROI({
          FileId: currentFileId,
          PageNo: currentPageNumber,
          Xaxis: coordinates?.left,
          Yaxis: coordinates?.top,
          Width: coordinates?.width,
          Height: coordinates?.height
        }).unwrap();
        if (mode !== "'footer'") {
          setAddEditPatternType(mode);
        }

        setheaderData({ patternTextFromROI: roiData?.text });
        if (mode !== "'footer'") {
          setAddInsertInitialData(mode === 'ADD' ? { patternTextFromROI: roiData?.text } : selectedPattern);
        }
      }
      onRubberbandDialogDismiss();
    };

    const onSubmitAddEditPattern = async () => {
      try {
        if (!patternFormRef.current) return;
        setAddEditPatternIsSaving(true);
        const values: any = patternFormRef.current.values || {};
        await handleAddNewPattern({
          patternROI: newRoi,
          mode: addEditPatternType,
          ...values
        });
        setAddEditPatternType(false);
      } catch (e) {
        console.error(e);
      } finally {
        setAddEditPatternIsSaving(false);
      }
    };

    const firstPart = selectedTechniqueName
      ? selectedTechniqueName.includes('Nested Lookup Printed')
        ? 'NestedLookupPrinted'
        : selectedTechniqueName.includes('Nested - Keyvaluepair printed')
        ? 'NestedKeyvaluepairprinted'
        : selectedTechniqueName.toLowerCase().includes('lookup printed')
        ? 'Lookup'
        : selectedTechniqueName.toUpperCase().includes('KEYVALUEPAIR')
        ? 'KeyValuePair'
        : selectedTechniqueName.split('-')[0]?.trim()
      : '';

    return (
      <Stack
        flexDirection="column"
        sx={{
          height: '100%',
          width: '100%'
        }}
      >
        {isDesktop && (
          <Titlebar
            fileLevelDetails={fileLevelDetails}
            handleFileNavigation={handleFileNavigation}
            isSecondaryScreen={isSecondaryScreen}
            patterns={patterns}
            selectedUser={selectedUser}
            setSelectedUser={setSelectedUser}
            finishAndMoveNextBatch={finishAndMoveNextBatch}
            batchList={batchList}
            selectedBatchId={selectedBatchId}
            isVerifiedBatch={isVerifiedBatch}
          />
        )}
        <Box className="right-pane-content" style={{ position: 'relative' }}>
          {!dualScreenEnabled && (
            <CanvasToolbar
              onRotateLeft={onRotateLeft}
              onRotateRight={onRotateRight}
              onZoomIn={onZoomIn}
              onZoomOut={onZoomOut}
              onRubberbandSelect={onRubberbandSelect}
              onRubberbandUnSelect={onRubberbandUnSelect}
              onFitToScreenSelect={onFitToScreenSelect}
              onFitToScreenUnSelect={onFitToScreenUnSelect}
              onDualScreen={onDualScreen}
              isSecondaryScreen={isSecondaryScreen}
              currentZoomLevel={currentZoomLevel}
              isMaxZoomIn={isMaxZoomIn}
              isMaxZoomOut={isMaxZoomOut}
              currentPage={currentPageNumber}
              onPageNumberChange={setCurrentPageNumber}
              totalPages={fileLevelDetails?.totalPages ?? 0}
              searchTextInFile={searchTextInFile}
              onSelectSearchTextInFile={handleOnHighlightRubberBandBySearch as any}
              fitToScreen={fitToScreen}
            />
          )}
          {isLabelRubberbandEnabled && (
            <Box
              className="configuration-lable-enable"
              sx={{
                position: 'absolute',
                top: !isMobile ? '55px' : 'auto',
                bottom: isMobile ? '65px' : 0,
                left: '50%', // Center horizontally
                transform: 'translateX(-50%)', // Adjust to center properly
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 150
              }}
            >
              <Alert color="secondary" className="configuration-lable-enable-alert" icon={<InfoOutlined />}>
                Please rubber-band the <strong>{currentFieldLabel || 'Label'}</strong> to continue
              </Alert>
            </Box>
          )}
          {!selectedPattern && (
            <Box
              className="configuration-lable-enable"
              sx={{
                position: 'absolute', // Ensure it's overlayed
                top: !isMobile ? '55px' : 'auto',
                bottom: isMobile ? '65px' : 0,
                left: '50%', // Center horizontally
                transform: 'translateX(-50%)', // Adjust to center properly
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 150
              }}
            >
              <Alert color="secondary" className="configuration-lable-enable-alert" icon={<InfoOutlined />}>
                Please selecte a pattern to load the image.
              </Alert>
            </Box>
          )}
          {isVerifiedBatch && (
            <Box
              className="configuration-lable-enable"
              sx={{
                position: 'absolute', // Ensure it's overlayed
                top: !isMobile ? '55px' : 'auto',
                bottom: isMobile ? '65px' : 0,
                left: 0,
                right: 0,
                zIndex: 150
              }}
            >
              <Box
                className="configuration-lable-enable"
                sx={{
                  position: 'absolute', // Ensure it's overlayed
                  top: '0px',
                  left: '50%', // Center horizontally
                  transform: 'translateX(-50%)', // Adjust to center properly
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 151
                }}
              >
                <Alert color="error" className="configuration-lable-enable-alert" icon={<WarningAmberIcon />}>
                  You are not allowed to add/edit the pattern for Learning finished batches
                </Alert>
              </Box>
            </Box>
          )}
          {!fitToScreen && (
            <ScrollNavigator
              onScrollUp={onScrollUp}
              onScrollRight={onScrollRight}
              onScrollDown={onScrollDown}
              onScrollLeft={onScrollLeft}
            />
          )}
          <Box className="canvas-wrapper" ref={canvasWrapperRef}>
            <FabricImageCanvas canvasRef={canvasRef} />
            {(isLoadingImageBytes || imageLoading) && (
              <Box className="configuration-loader-wrapper">{selectedPattern && <CircularProgress />}</Box>
            )}
          </Box>
        </Box>

        <Dialog
          open={showRubberbandDialog}
          onClose={() => {
            setShowLabelRubberbandDialog(false);
            disableLabelRubberband();
          }}
          BackdropProps={{
            style: {
              background: 'transparent'
            }
          }}
          PaperProps={{
            style: {
              position: 'absolute',
              margin: 0,
              padding: '5px',
              left: `${rubberbandDialogPosition?.left}px`,
              top: `${rubberbandDialogPosition?.top}px`,
              borderRadius: '8px',
              border: '1px solid #9E9E9D',
              background: '#E2E2E2',
              boxShadow: '0px 4px 8px 0px rgba(158, 158, 157, 0.50)'
            }
          }}
        >
          <Stack direction="row">
            {!rubberbandDialogPosition?.isEdit ? (
              <Tooltip title="Add">
                <IconButtonExtended shape="rounded" color="success" onClick={() => onConfirmUpdateFieldROI('ADD')}>
                  <AddCircleOutlineRoundedIcon />
                </IconButtonExtended>
              </Tooltip>
            ) : (
              <Tooltip title="Edit">
                <IconButtonExtended shape="rounded" color="info" onClick={() => onConfirmUpdateFieldROI('EDIT')}>
                  <EditIcon />
                </IconButtonExtended>
              </Tooltip>
            )}

            <Tooltip title="Clear">
              <IconButtonExtended shape="rounded" color="error" onClick={onRubberbandDialogDismiss}>
                <CloseOutlined />
              </IconButtonExtended>
            </Tooltip>
          </Stack>
        </Dialog>
        <AssuranceAddPatternDialog
          open={Boolean(addEditPatternType)}
          addEditPatternType={addEditPatternType}
          headerData={headerData}
          fileName={fileLevelDetails?.fileName || ''}
          addInsertInitialData={addInsertInitialData}
          setshowDialog={setshowDialog}
          rectImageContent={rectImageContent || ''}
          onCancel={() => setAddEditPatternType(false)}
          setAddEditPatternType={setAddEditPatternType}
          isSubmitLoading={addEditPatternIsSaving}
          onSubmit={onSubmitAddEditPattern}
          selectedFieldName={selectedFieldName || ''}
          selectedTechniqueName={firstPart}
          patternFormRef={patternFormRef}
        />
        {showDialog && (
          <>
            <Dialog
              open={showLabelRubberbandDialog}
              onClose={() => {
                setShowLabelRubberbandDialog(false);
                disableLabelRubberband();
              }}
              BackdropProps={{
                style: {
                  background: 'transparent'
                }
              }}
              PaperProps={{
                style: {
                  position: 'absolute',
                  margin: 0,
                  padding: '5px',
                  left: `${rubberbandDialogPosition?.left}px`,
                  top: `${rubberbandDialogPosition?.top}px`,
                  borderRadius: '8px',
                  border: '1px solid #9E9E9D',
                  background: '#E2E2E2',
                  boxShadow: '0px 4px 8px 0px rgba(158, 158, 157, 0.50)'
                }
              }}
            >
              <Box sx={{ padding: '5px' }}>
                <Tooltip title={translate('pages.review.assurance.labels.add')}>
                  <IconButtonExtended
                    shape="rounded"
                    color="success"
                    onClick={() => {
                      setShowRubberbandDialog(false);
                      setShowLabelRubberbandDialog(false);
                      disableLabelRubberband();
                      setAddEditPatternType(true);
                      onConfirmUpdateFieldROI('footer');
                    }}
                  >
                    <DoneOutlined />
                  </IconButtonExtended>
                </Tooltip>
                <Tooltip title={translate('pages.review.assurance.labels.clear')}>
                  <IconButtonExtended
                    shape="rounded"
                    color="error"
                    onClick={() => {
                      disableLabelRubberband();
                      setShowLabelRubberbandDialog(false);
                    }}
                  >
                    <CloseOutlined />
                  </IconButtonExtended>
                </Tooltip>
              </Box>
            </Dialog>
          </>
        )}
      </Stack>
    );
  }
);

export default ImageViewerRightPane;
